export type GpSurfacesMapClause = 'gp' | 'new';

export const DEFAULT_NGP_CAMPAIGN_YEARS = [2025, 2026, 2027] as const;

export type GpSurfacesMapOrganization = {
  id: number;
  name: string;
};

export type GpSurfacesMapDepartment = {
  code: string;
  name: string;
  total_area: number;
  parcelle_count: number;
  ilot_count: number;
  carbon_points: number;
  analysis_count: number;
  granulo_points: number;
  granulo_analysis_count: number;
  total_sampling_points: number;
  total_analysis_count: number;
};

export type GpSurfacesMapSummary = {
  total_area: number;
  parcelle_count: number;
  department_count: number;
  ilot_count: number;
  carbon_points: number;
  analysis_count: number;
  granulo_points: number;
  granulo_analysis_count: number;
  total_sampling_points: number;
  total_analysis_count: number;
};

export type GpSurfacesMapProjectionDepartment = {
  code: string;
  name: string;
  projected_area: number;
  projected_volume_t: number;
  combined_area: number;
  carbon_points: number;
  analysis_count: number;
  granulo_points: number;
  granulo_analysis_count: number;
  total_sampling_points: number;
  total_analysis_count: number;
};

export type GpSurfacesMapProjectionSummary = {
  projected_area: number;
  projected_volume_t: number;
  combined_area: number;
  carbon_points: number;
  analysis_count: number;
  granulo_points: number;
  granulo_analysis_count: number;
  total_sampling_points: number;
  total_analysis_count: number;
};

export type GpSurfacesMapProjectionOrganization = {
  id: number;
  name: string;
  volume_t: number;
  manual_volume_t: number;
  projected_area: number;
  ratio_ha_per_t: number | null;
  warning: string | null;
};

export type GpSurfacesMapSamplerCoverageTotals = {
  actual_area: number;
  projected_area: number;
  combined_area: number;
  projected_volume_t: number;
  carbon_points: number;
  analysis_count: number;
  granulo_points: number;
  granulo_analysis_count: number;
  total_sampling_points: number;
  total_analysis_count: number;
};

export type GpSurfacesMapSamplerCoverageRow = {
  department_code: string;
  department_name: string;
  sampler_organization_id: number;
  sampler_organization_name: string;
  share_percent: number;
  color?: string | null;
  actual_area: number;
  projected_area: number;
  combined_area: number;
  projected_volume_t: number;
  carbon_points: number;
  analysis_count: number;
  granulo_points: number;
  granulo_analysis_count: number;
  total_sampling_points: number;
  total_analysis_count: number;
};

export type GpSurfacesMapSamplerCoverageShare = {
  sampler_organization_id: number;
  sampler_organization_name: string;
  share_percent: number;
  color?: string | null;
};

export type GpSurfacesMapSamplerCoverageDepartment = {
  code: string;
  name: string;
  total_share_percent: number;
  is_complete: boolean;
  uncovered_share_percent: number;
  dominant_sampler_organization_id: number | null;
  shares: GpSurfacesMapSamplerCoverageShare[];
};

export type GpSurfacesMapSamplerCoverageOrganization = {
  id: number;
  name: string;
  color?: string | null;
  totals: GpSurfacesMapSamplerCoverageTotals;
};

export type GpSurfacesMapSamplerCoverage = {
  rows: GpSurfacesMapSamplerCoverageRow[];
  departments: GpSurfacesMapSamplerCoverageDepartment[];
  sampler_organizations: GpSurfacesMapSamplerCoverageOrganization[];
  unassigned_totals: GpSurfacesMapSamplerCoverageTotals;
};

export type GpSurfacesMapProjection = {
  all_campaigns: boolean;
  enabled: boolean;
  uncertain_volume_discount: boolean;
  uncertain_volume_factor: number | null;
  manual_yield_t_per_ha: number;
  summary: GpSurfacesMapProjectionSummary;
  departments: GpSurfacesMapProjectionDepartment[];
  organizations: GpSurfacesMapProjectionOrganization[];
  warnings: string[];
};

export type GpSurfacesMapResponse = {
  clause: GpSurfacesMapClause;
  grandfather_clause: boolean;
  campaign_year: number | null;
  available_campaign_years: number[];
  reference_campaign_year: number;
  summary: GpSurfacesMapSummary;
  departments: GpSurfacesMapDepartment[];
  projection: GpSurfacesMapProjection | null;
  sampler_coverage: GpSurfacesMapSamplerCoverage | null;
  filters: {
    organizations: GpSurfacesMapOrganization[];
  };
};

export type GpSurfacesMapContentMode = 'surfaces' | 'sampler_coverage';

export type GpSurfacesMapViewMode = 'actual' | 'with_projection';

export type GpSurfacesMapDisplayMode = 'overview' | 'interactive';

export type GpSurfacesMapProjectionVolumeMode = 'declared' | 'conservative';

export type GpSurfacesMapMetricMode =
  | 'hectares'
  | 'carbon_points'
  | 'analysis_count'
  | 'granulo_points'
  | 'granulo_analysis_count'
  | 'total_sampling_points'
  | 'total_analysis_count';

export type GpSurfacesMapParams = {
  clause?: GpSurfacesMapClause;
  campaignYear?: number | null;
  organizationIds?: number[];
  withProjection?: boolean;
  uncertainVolumeDiscount?: boolean;
  metricMode?: GpSurfacesMapMetricMode;
};

export type GpSurfacesMapMapDepartment = GpSurfacesMapDepartment & {
  actual_area: number;
  map_value: number;
  projected_area?: number;
  projected_volume_t?: number;
  combined_area?: number;
};
